import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './Steps.module.scss'
import { CustomerFlows, FlowAlternatives, InputGroupKeys } from 'store/customer/enums'
import { useTranslation } from 'next-i18next/pages'
import PropositionCard from '../components/proposition-card/PropositionCard'
import PriceDrawer from 'components/price-drawer/PriceDrawer'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import useWindowSize from 'hooks/useWindowSize'
import PropositionCardSkeleton from '../components/proposition-card-skeleton/PropositionCardSkeleton'
import SubscribeDrawer from 'features/simulation/components/subscribe-drawer/SubscribeDrawer'
import { useGetProducersQuery } from 'store/api/bolt-api'
import { goToStep, selectProposition, setShowPlentyPropositions } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { shallowEqual } from 'react-redux'
import classNames from 'classnames'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { Proposition } from 'store/customer/types'
import { PROPOSITIONS_ORDER } from 'features/registration/constants'
import { checkCanApplyPlentyPromo, getUsableProducts } from 'utils/products'
import { RegistrationSteps } from 'features/registration/types'
import { CampaignGTMEvents } from 'types/tracking'
import { Heading, Switch } from '@boltenergy-be/design-system'
import { useGetCmsProductsQuery } from 'store/api/cms-api'
import { Language } from 'types/language'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { MarketingDataKeys } from 'store/marketing/types'
import { updateMarketingData } from 'store/marketing/slice'
import PlentyModal from 'features/simulation/components/plenty-modal/PlentyModal'
import { setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import { checkEnforcePlenty, requestAndSavePropositions } from 'features/simulation/utils'
import { EV_FLEX_SIMULATION_SO } from 'features/bolt-drive-flex/BoltDriveFlex.constants'
import { FEATURE_FLAGS } from 'constants/tracking'
import { useFeatureFlagTranslatedPayload } from 'hooks/useFeatureFlagTranslatedPayload'
import {
  CUSTOMER_TYPES,
  OPT_IN_CTAS,
  PLENTY_PRODUCTS,
  Product,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCTS,
  PRODUCTS_CONFIG,
  REGIONS
} from '@boltenergy-be/shared'
import { BENEFITS_AT_WORK_SIMULATION_SO } from 'features/benefits-at-work/BenefitsAtWork.constants'
import { formatAmount } from 'utils/format'
import parse from 'html-react-parser'
import { getCo2AvoidanceForElectricityVolume } from 'utils/usage'
import { log } from 'utils/logging'
import { Criticality, Flow } from 'types/logging'
import EnforcePlentyPropositionBanner from 'features/simulation/components/enforce-plenty-proposition-banner/EnforcePlentyPropositionBanner'
import { getProductPromoForDate } from 'components/promo-code/utils'

const PropositionStep = () => {
  // Redux store
  const { selectedProducer } = useStoreSelector((store) => store.app)
  const { inputs, propositions, flows, flowAlternative } = useStoreSelector((store) => store.customer)
  const { optinCta, simulationSo, availableProducts } = useStoreSelector((store) => store.marketing)
  const { isCompany } = inputs[InputGroupKeys.PERSONAL_DATA]
  const { meterType, usage } = inputs[InputGroupKeys.SIMULATION]
  const { region } = inputs[InputGroupKeys.SIMULATION]
  const { contactMe, isLoading, error } = flows[CustomerFlows.SIMULATION]
  const { simulatedPropositions, simulateSpecificProduct, showPlentyPropositions } = propositions
  const dispatch = useStoreDispatch()

  // Router
  const router = useRouter()
  const { locale } = router

  // RTK queries
  const { data: producers } = useGetProducersQuery()
  const { data: productsContent, isLoading: isFetchingProductsContent } = useGetCmsProductsQuery({ language: locale as Language })

  // References
  const scrollableContainerRef = useRef<HTMLDivElement>(null)

  // Window size
  const { isTablet, isDesktop } = useWindowSize()

  // i18n
  const { t } = useTranslation(['simulation', 'common'])

  // Posthog
  const recommendedText = useFeatureFlagTranslatedPayload({
    key: FEATURE_FLAGS.recommendedProductText,
    fallback: t('common:recommended')
  })

  // Local state
  const [priceDetails, setPriceDetails] = useState<Product | null>(null)
  const [subscribeDrawerOpen, setSubscribeDrawerOpen] = useState<boolean>(false)
  const [plentyModalOpen, setPlentyModalOpen] = useState<boolean>(false)
  const [omitLoadingState, setOmitLoadingState] = useState<boolean>(false)

  // Constants
  // getUsableProducts reads the store itself; selecting it with shallowEqual keeps the array reference stable for the memos below
  const usableProducts = useStoreSelector(() => getUsableProducts({ region }), shallowEqual)
  const availableSpecificProduct = simulateSpecificProduct && usableProducts.includes(simulateSpecificProduct)
  const isLeadGenCampaign = flowAlternative[FlowAlternatives.TELESALES_CAMPAIGN]
  const isPlentyFlow = flowAlternative[FlowAlternatives.PLENTY]
  const enforcePlenty = checkEnforcePlenty({ region, availableProducts })
  const onlyPlentyAvailable = usableProducts.every((product) => PRODUCTS_CONFIG[product].isPlentyProduct)
  const hidePlentySlider =
    onlyPlentyAvailable ||
    enforcePlenty ||
    [EV_FLEX_SIMULATION_SO, BENEFITS_AT_WORK_SIMULATION_SO].includes(simulationSo) ||
    (availableProducts && !availableProducts[region]?.some((p) => PLENTY_PRODUCTS.includes(p)))

  const pageTitle = useMemo<string>(() => {
    const co2Reduction = getCo2AvoidanceForElectricityVolume(meterType, usage)
    return t('simulation:proposition.title', { reduction: formatAmount(co2Reduction) })
  }, [meterType, t, usage])

  const sortedPropositions = useMemo(() => {
    // If the user has selected a specific product, return only that product in the form of a Object.entries array
    if (availableSpecificProduct) {
      const availableSpecificCounterpartProduct =
        PRODUCTS_CONFIG[simulateSpecificProduct]?.[PRODUCT_CONFIG_OPTIONS.counterpart] || undefined
      return (
        showPlentyPropositions && availableSpecificCounterpartProduct
          ? [[availableSpecificCounterpartProduct, simulatedPropositions[availableSpecificCounterpartProduct]]]
          : [[simulateSpecificProduct, simulatedPropositions[simulateSpecificProduct]]]
      ) as [Product, Proposition][]
    }

    const entries: [string, Proposition][] = simulatedPropositions ? Object.entries(simulatedPropositions) : []
    const order = PROPOSITIONS_ORDER

    const productsOrderWithCorrectedProducts =
      showPlentyPropositions || enforcePlenty ? order.map((product) => PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.counterpart]) : order

    const filteredOrder = productsOrderWithCorrectedProducts.filter(
      (p) =>
        usableProducts.includes(p) &&
        entries.some(([product]) => {
          if (simulationSo === EV_FLEX_SIMULATION_SO && p === PRODUCTS.fix) {
            return false
          }

          return product === p
        })
    )

    // Sort the propositions by the predefined order & return it
    return simulatedPropositions && productsContent
      ? filteredOrder.map((product) => [product, simulatedPropositions[product]] as [Product, Proposition])
      : []
  }, [
    simulationSo,
    enforcePlenty,
    showPlentyPropositions,
    availableSpecificProduct,
    productsContent,
    simulateSpecificProduct,
    simulatedPropositions,
    usableProducts
  ])
  const enforcePlentyWithMultipleProducts = enforcePlenty && sortedPropositions.length > 1

  // Return of the local state
  const [subscribeDrawerProduct, setSubscribeDrawerProduct] = useState<Product>(
    availableSpecificProduct ? propositions.simulateSpecificProduct : PRODUCTS.variableOffline
  )

  // Opens the subscribe drawer if the contactMe input has a value
  useEffect(() => {
    if (contactMe) {
      setSubscribeDrawerOpen(true)
      setSubscribeDrawerProduct(contactMe)
    }
  }, [contactMe])

  /**
   * Handles the intersection of the observer & class assignment/removal
   * @param {IntersectionObserverEntry[]} entries
   * @param {IntersectionObserver} observer
   */
  const handleIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    const root = scrollableContainerRef.current

    if (root) {
      entries.forEach((entry) => {
        if (entry.target === observer.root.lastElementChild) {
          entry.isIntersecting ? root.classList.remove('is-at-end') : root.classList.add('is-at-end')
        }

        if (entry.target === observer.root.firstElementChild) {
          entry.isIntersecting ? root.classList.remove('is-at-start') : root.classList.add('is-at-start')
        }
      })
    }
  }

  // Initiate & clean up the IntersectionObserver
  useEffect(() => {
    const container = scrollableContainerRef.current

    // Initiate the IntersectionObserver
    const observer = new IntersectionObserver(handleIntersection, {
      root: container,
      threshold: 1
    })

    const propositionCards = Array.from(container.children)

    // Observe reference children
    propositionCards.forEach((card) => observer.observe(card))

    // Clean up useEffect
    return () => propositionCards.forEach((card) => observer.unobserve(card))
  }, [])

  /**
   * Returns the tag for the proposition card
   * @param {Product} product
   * @returns {string | null}
   */
  const getPropositionTag = (product: Product): string | null => {
    if (simulateSpecificProduct && usableProducts.includes(simulateSpecificProduct)) return t('proposition.yourChoice', 'Jouw keuze')
    if (usableProducts.length > 1 && !enforcePlenty) {
      if (productsContent?.[product]?.recommended) return recommendedText
    }

    return null
  }

  /**
   * Handles the selection of a proposition
   * @param {Product} product
   */
  const handleSelectProposition = async (product: Product) => {
    trackEvent(
      { category: 'simulation', object: 'proposition', action: 'click' },
      { product, amount_of_propositions: sortedPropositions.length }
    )

    if (enforcePlenty && !showPlentyPropositions) {
      dispatch(setShowPlentyPropositions(true))
    }

    // Update OptinCta to switch now
    dispatch(updateMarketingData({ key: MarketingDataKeys.OPTIN_CTA, value: OPT_IN_CTAS.switchNow }))
    // Select the current product
    dispatch(selectProposition({ productType: product as Product }))
    // Go back to the first step of the registration
    dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.DELIVERY_ADDRESS }))

    // Apply promocode if necessary
    if (checkCanApplyPlentyPromo(product)) {
      setOmitLoadingState(true)
      const currentPromo = getProductPromoForDate(product)
      try {
        const { valid } = await setPromoOrReferralCodeInStore(
          isCompany ? currentPromo?.code[CUSTOMER_TYPES.professional] : currentPromo?.code[CUSTOMER_TYPES.residential]
        )
        if (valid) {
          // request and save new propositions with the promocode. Don't await it, we don't need to wait for it
          requestAndSavePropositions()
        }
      } catch {
        // Promo code check failed — continue to registration without it
        log({
          error: 'Failed to process promo code',
          identifier: `[${Flow.SIMULATION}:selectProposition]`,
          criticality: Criticality.HIGH,
          metaData: { failedPromo: { ...currentPromo, product: product } }
        })
      }
    }

    // Redirect to the registration flow
    if (selectedProducer) {
      router.push(routes(locale, selectedProducer).registration)
    }
  }

  /**
   * Handles the opening of the subscribe drawer and the required tracking
   * @param {Product} product
   */
  const handleOpenSubscribeDrawer = (product: Product) => {
    if (isLeadGenCampaign) {
      trackGTMEvent(CampaignGTMEvents.TELESALES_CLICK)
    }

    if (optinCta === OPT_IN_CTAS.switchNow) {
      dispatch(
        updateMarketingData({
          key: MarketingDataKeys.OPTIN_CTA,
          value: isLeadGenCampaign ? OPT_IN_CTAS.contactMe : OPT_IN_CTAS.sendDetails
        })
      )
    }

    setSubscribeDrawerProduct(product)
    setSubscribeDrawerOpen(true)
  }

  // Prefetch the registration page
  // (pre-fetching is a production-only feature, to test, run yarn build & yarn start)
  useEffect(() => {
    // Prefetching is best-effort: it rejects with "Failed to load static props" whenever the page data can't be
    // fetched (stale deploy, flaky connection, ...). Swallow it so it doesn't become an unhandled rejection.
    router.prefetch(routes(locale, selectedProducer).registration).catch(() => {})
  }, [selectedProducer, locale, router])

  return (
    <>
      <Heading as="h1" variant="h3" serif>
        {parse(pageTitle)}
      </Heading>

      <div className={styles['propositions-wrapper']}>
        <div
          ref={scrollableContainerRef}
          data-count={sortedPropositions?.length || 0}
          className={classNames(styles.propositions, { [styles.solo]: sortedPropositions?.length === 1 || !!availableSpecificProduct })}
          style={{
            gridTemplateColumns:
              sortedPropositions?.length > 1 && !availableSpecificProduct
                ? `repeat(${sortedPropositions.length}, ${isTablet ? 'minmax(260px, 1fr)' : sortedPropositions?.length === 2 && isDesktop ? '1fr' : '260px'})`
                : '1fr'
          }}
        >
          {!error && !omitLoadingState && (sortedPropositions.length === 0 || isLoading || isFetchingProductsContent) ? (
            sortedPropositions?.length ? (
              sortedPropositions.map(([product]) => <PropositionCardSkeleton key={`loader-${product}`} />)
            ) : (
              <PropositionCardSkeleton />
            )
          ) : (
            <>
              {!!sortedPropositions.length && sortedPropositions.every((p) => p?.[1]?.price) ? (
                sortedPropositions.map(([product, proposition]) => {
                  const counterpartProduct = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.counterpart]

                  return (
                    <PropositionCard
                      variant={enforcePlenty ? (enforcePlentyWithMultipleProducts ? 'plenty-multiple' : 'plenty-single') : 'default'}
                      showPlentyModal={() => setPlentyModalOpen(true)}
                      isSingleProposition={sortedPropositions.length === 1 || !!availableSpecificProduct}
                      key={product}
                      proposition={proposition}
                      counterpartProposition={
                        !enforcePlenty && showPlentyPropositions && counterpartProduct
                          ? simulatedPropositions[counterpartProduct]
                          : undefined
                      }
                      icon={productsContent?.[product]?.icon}
                      title={productsContent?.[product]?.name}
                      tag={getPropositionTag(product)}
                      isLoading={isLoading}
                      primaryAction={{
                        onClick: async () => {
                          if (isLeadGenCampaign) {
                            handleOpenSubscribeDrawer(product)
                          } else {
                            await handleSelectProposition(product)
                          }
                        },
                        label: isLeadGenCampaign ? t('simulation:leadGenCampaign.primaryAction') : t('simulation:proposition.submit')
                      }}
                      secondaryAction={{
                        onClick: () => setPriceDetails(product as Product),
                        label: t('simulation:proposition.viewPrice')
                      }}
                      ternaryAction={{
                        onClick: async () => {
                          if (isLeadGenCampaign) {
                            await handleSelectProposition(product)
                          } else {
                            handleOpenSubscribeDrawer(product)
                          }
                        },
                        label: isLeadGenCampaign ? t('simulation:leadGenCampaign.ternaryAction') : t('simulation:proposition.sendEmail')
                      }}
                    >
                      {(productsContent[product].alternativePerks || productsContent[product].perks) && (
                        <>
                          {inputs.simulation.region !== REGIONS.flanders && productsContent?.[product]?.alternativePerks ? (
                            <BlocksRenderer content={productsContent[product].alternativePerks} />
                          ) : (
                            <BlocksRenderer content={productsContent[product].perks} />
                          )}
                        </>
                      )}
                    </PropositionCard>
                  )
                })
              ) : (
                <>
                  {!isLoading && (
                    <div className={styles.failed}>
                      <Heading as="h3" variant="h4">
                        {t('simulation:error.title')}
                      </Heading>
                      <p>{t('simulation:error.description')}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {!isPlentyFlow && !hidePlentySlider && (
        <div className={styles.plenty}>
          <Heading as="h2" variant="h6" serif>
            {t('simulation:proposition.plenty.title')}
          </Heading>
          <Switch
            checked={!!showPlentyPropositions}
            onChange={() => setPlentyModalOpen(true)}
            label={t('simulation:proposition.plenty.switch')}
          />
        </div>
      )}

      {enforcePlentyWithMultipleProducts && hidePlentySlider && (
        <EnforcePlentyPropositionBanner position="underneath" showPlentyModal={() => setPlentyModalOpen(true)} />
      )}

      <PlentyModal onRequestClose={() => setPlentyModalOpen(false)} isOpen={plentyModalOpen} enforcedPlenty={enforcePlenty} />

      <PriceDrawer
        isOpen={!!priceDetails}
        onRequestClose={() => setPriceDetails(null)}
        producer={producers?.[selectedProducer]}
        product={priceDetails}
      />

      <SubscribeDrawer
        product={subscribeDrawerProduct}
        isOpen={!!subscribeDrawerOpen}
        onRequestClose={() => setSubscribeDrawerOpen(false)}
      />
    </>
  )
}

export default PropositionStep
